/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import type { Prompt } from "@1js/pl-card-components";
import {
  FeedbackHostPlatformType,
  FeedbackUiType,
  openFeedback as inAppOpenFeedback,
  type IFeedbackInitOptions,
  type IFeedbackTelemetry,
} from "@ms/inapp-feedback-loader";
import { UAParser } from "ua-parser-js";
import {
  getAgeGroup,
  getAuthenticationType,
  getComplianceChecks,
} from "./compliance";

declare global {
  interface Window {
    setFeedbackContextData: (contextData: Prompt) => void;

    initializeFeedback: (
      centroHostname: string,
      language: string
    ) => Promise<void>;

    openFeedback: (
      isProduction: boolean,
      appId: number,
      userEmail: string,
      authType: string,
      userId: string,
      tenantId: string,
      ageGroup: string
    ) => Promise<void>;
  }
}

/**
 * Additional contextual data attached to OCV feedback items
 * This variable holders either the last clicked Prompt on the homepage, or
 * the prompt details if the user is on the prompt details page.
 */
let feedbackContextData: Prompt | undefined = undefined;

/**
 * Set optional context data for OCV feedback items
 * @param contextData Last clicked prompt, or prompt details if the user is on prompt detail
 */
window.setFeedbackContextData = function (contextData: Prompt) {
  if (!contextData) return;
  feedbackContextData = contextData;
};

/**
 * Open OCV feedback modal
 */
export async function openFeedback(
  isProduction: boolean,
  appId: number,
  userEmail: string,
  authType: string,
  userId: string,
  tenantId: string,
  ageGroup: string
) {
  const authenticationType = getAuthenticationType(authType);
  const feedbackOptions: IFeedbackInitOptions = {
    appId,
    authenticationType,
    userId,
    tenantId,
    isProduction,
    feedbackConfig: {
      diagnosticsConfig: {
        isDiagnosticsIncluded: true,
        isShareContextDataEnabled: feedbackContextData !== undefined,
        isHostManagedContextDataEnabled: feedbackContextData !== undefined,
        getContextData: () => {
          if (!feedbackContextData)
            return new Promise((resolve) => resolve([]));

          const promptDetailsFile = new File(
            [
              JSON.stringify({
                CommandText: feedbackContextData.CommandText,
                PromptId: feedbackContextData.PromptId,
              }),
            ],
            "promptDetails.txt",
            {
              type: "text/plain",
            }
          );
          return new Promise((resolve) => resolve([promptDetailsFile]));
        },
      },
      isEmailCollectionEnabled: true,
      isFeedbackForumEnabled: false,
      isFileUploadEnabled: true,
      isScreenshotEnabled: true,
      isMyFeedbackEnabled: true,
      myFeedbackUrl: "https://feedbackportal.microsoft.com/feedback/mycontent/",
      isScreenRecordingEnabled: true,
      isThankYouPageDisabled: false,
      displayAdditionalInformationEnabled: false,
      hostPlatform: FeedbackHostPlatformType.Web,
      email: userEmail,
      isDisplayed: true,
      feedbackUiType: FeedbackUiType.SidePane,
      complianceChecks: await getComplianceChecks(authenticationType),
    },
    ageGroup: getAgeGroup(parseInt(ageGroup, 10)),
    telemetry: getFeedbackTelemetry(),
    themeOptions: {
      baseTheme: "M365Fluent2LightTheme",
      colorScheme: {
        palette: {
          themePrimary: "#464FEB",
          themeLighterAlt: "#f7f7fe",
          themeLighter: "#e0e1fc",
          themeLight: "#c5c7f9",
          themeTertiary: "#8d92f3",
          themeSecondary: "#5b62ed",
          themeDarkAlt: "#3f47d3",
          themeDark: "#353cb2",
          themeDarker: "#272c83",
          neutralLighterAlt: "#faf9f8",
          neutralLighter: "#f3f2f1",
          neutralLight: "#edebe9",
          neutralQuaternaryAlt: "#e1dfdd",
          neutralQuaternary: "#d0d0d0",
          neutralTertiaryAlt: "#c8c6c4",
          neutralTertiary: "#a19f9d",
          neutralSecondary: "#605e5c",
          neutralSecondaryAlt: "#8a8886",
          neutralPrimaryAlt: "#3b3a39",
          neutralPrimary: "#323130",
          neutralDark: "#201f1e",
          black: "#000000",
          white: "#ffffff",
        },
      },
    },
  };

  inAppOpenFeedback(feedbackOptions);
}

function getFeedbackTelemetry(): IFeedbackTelemetry {
  const { os, browser, device } = new UAParser().getResult();
  return {
    browser: browser.name,
    browserVersion: browser.version,
    deviceType: device.type,
    platform: `${os.name} ${os.version}`,
    sourcePageName: document.title,
    sourcePageURI: `${location.protocol}//${location.host}${location.pathname}`, // construct URI without querystrings to avoid PII leaks
  };
}
